import { useQueries } from 'react-query'
import cookies from 'js-cookie'
import { useContrastQuery } from '../../../../hooks/use-contrast-query'
import {
  CliLibraryFilter,
  LibraryFilterType
} from '../../helpers/libraries-interfaces'
import qs from 'qs'
import { ColumnSort } from '@tanstack/react-table'

export interface LibraryFilterQueryFormData {
  quickFilter: string
  apps: string[]
  servers: string[]
  tags: string[]
  languages: string[]
  environments: string[]
  licenses: string[]
  grades: string[]
  status: string[]
  severities: string[]
  includeUsed: boolean
  includeUnused: boolean
  q: string
}

export interface GetLibraryDataProps {
  url: string
  method: string
  formData?: LibraryFilterQueryFormData
}

export const getData = async ({
  url,
  formData,
  method
}: GetLibraryDataProps): Promise<any> => {
  let res

  try {
    res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        charset: 'utf-8',
        'X-XSRF-TOKEN': cookies.get('XSRF-TOKEN')
      },
      body: formData ? JSON.stringify(formData) : undefined
    })
  } catch {
    throw Error('could not fetch the data for that resource')
  }

  if (res.status >= 500) {
    throw Error('could not fetch the data for that resource')
  }

  return res.json()
}

const SortingOrderField = {
  grade: 'score',
  file_name: 'fileName',
  Usage: 'usage'
}

export const useLibraries = ({
  formData,
  keepPreviousData,
  organizationID,
  pageIndex,
  pageSize,
  sortingOrder,
  enabled,
  onSettled
}: {
  formData: LibraryFilterQueryFormData
  keepPreviousData: boolean
  organizationID: string
  pageIndex: number
  pageSize: number
  sortingOrder: ColumnSort[]
  enabled: boolean
  onSettled?: () => void
}) => {
  const url = `/Contrast/api/ng/${organizationID}/libraries/filter?expand=skip_links,apps,quickFilters,vulns,status,usage_counts&offset=${
    pageIndex * pageSize
  }&limit=${pageSize}&sort=${sortingOrder[0].desc ? '-' : ''}${
    SortingOrderField[sortingOrder[0].id]
  }`

  return useContrastQuery(
    ['getLibraryData', url, formData],
    getData,
    {
      url,
      formData,
      method: 'POST'
    },
    { keepPreviousData, enabled, onSettled }
  )
}

export const assesLibraryFilters = [
  LibraryFilterType.Apps,
  LibraryFilterType.Tags,
  LibraryFilterType.Grades,
  LibraryFilterType.Languages,
  LibraryFilterType.Usage,
  LibraryFilterType.Licenses,
  LibraryFilterType.Environments,
  LibraryFilterType.Servers,
  LibraryFilterType.Status,
  LibraryFilterType.Severities
]

export const useLibraryFilters = ({
  organizationID,
  enabled,
  formData
}: {
  organizationID: string
  enabled: boolean
  formData?: LibraryFilterQueryFormData
}) => {
  return useQueries(
    assesLibraryFilters.map((filter) => {
      const url = `/Contrast/api/ng/${organizationID}/libraries/filters/${filter.toLowerCase()}/listing?expand=skip_links`
      return {
        queryKey: ['getLibraryFilters', url, formData],
        queryFn: async () => {
          const data = await getData({
            url,
            formData,
            method: 'POST'
          })
          return {
            ...data,
            filterType: filter
          }
        },
        keepPreviousData: true,
        enabled
      }
    })
  )
}

export const useAgentFeatures = (enabled: boolean) =>
  useContrastQuery(
    ['getAgentFeatures', `/Contrast/api/ng/agent/feature`],
    getData,
    {
      url: `/Contrast/api/ng/agent/feature`,
      method: 'GET'
    },
    { enabled }
  )

export const useCLiLibraries = (url: string, enabled: boolean) => {
  return useContrastQuery(
    ['useCLiLibraries', url],
    getData,
    {
      url,
      method: 'GET'
    },
    {
      keepPreviousData: true,
      enabled
    }
  )
}

export const cliFilters = [
  LibraryFilterType.Severity,
  LibraryFilterType.QuickFilter, // Different from the way Assess Grid loads it's quick filters
  LibraryFilterType.Licenses,
  LibraryFilterType.Languages,
  LibraryFilterType.Repos,
  LibraryFilterType.Projects
]

export const useCliLibraryFilters = (
  organizationID: string,
  enabled: boolean,
  applicationID?: string,
  searchParam?: string, // string with field and value and equals
  quickFilterParam?: string, // quickFilter value
  filterParams?: string // string with ampersands between each field and value
) => {
  const parsedFilterParams = qs.parse(filterParams || '')
  const parsedSearchParams = qs.parse(searchParam || '')

  return useQueries(
    cliFilters.map((filter) => {
      const url = `/Contrast/api/sca/organizations/${organizationID}/${
        applicationID ? `applications/${applicationID}/` : ''
      }libraries/filter/${filter}/listing${
        searchParam || filterParams || quickFilterParam ? '?' : ''
      }${qs.stringify({
        quickFilter: quickFilterParam,
        ...parsedFilterParams,
        ...parsedSearchParams
      })}`
      return {
        queryKey: ['getLibraryFilters', url, filterParams, searchParam],
        queryFn: async () => {
          const data = await getData({
            url,
            method: 'GET'
          })
          return {
            filters: data ? (data as CliLibraryFilter[]) : [],
            filterType: filter
          }
        },
        useErrorBoundary: (error) => error.response?.status >= 500,
        keepPreviousData: true,
        enabled
      }
    })
  )
}

export const getGridLibrary = async (
  organizationID: string,
  formData: LibraryFilterQueryFormData
): Promise<any> => {
  const url = `/Contrast/api/ng/${organizationID}/libraries/filter?expand=skip_links,apps,quickFilters,vulns,status,usage_counts&offset=0&limit=25`
  const lib = await getData({ url, formData, method: 'POST' })

  if (lib.libraries[0]) {
    return lib.libraries[0]
  }
}
